import { CheckCircle } from "@phosphor-icons/react";
import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import SearchInput from "common/components/SearchInput";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  dropdownMenuItemVariants,
} from "common/components/ui/DropdownMenu";
import ProfileImage from "common/components/ui/ProfileImage";
import useDebounce from "common/hooks/useDebounce";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile } from "common/types";

import useSearchOptions from "../../../../../../../datahooks/useSearchOptions";
import { useSearchParamsContext } from "../../SearchParamsContext";
import useMergedOptions from "../useMergedOptions";
import useSearchParams from "../useSearchParams";

import type { PROFILE_SEARCH_OPTIONS } from "../../../../../../../constants";

interface FollowersProps {
  optionsKey: keyof typeof PROFILE_SEARCH_OPTIONS;
}

export default function Followers({ optionsKey }: FollowersProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { searchParams } = useSearchParamsContext();

  const [inputValue, setInputValue] = useState("");
  const [debouncedValue] = useDebounce(inputValue, 300);

  const { setSearchParamValue } = useSearchParams(optionsKey);

  const { followers, suggestions } = useSearchOptions({
    followersKeywords: debouncedValue,
  });

  const suggestedOptions = suggestions ? suggestions[optionsKey] : [];
  const selectedAccount = searchParams[optionsKey] as LinkedInProfile;

  const mergedOptions = useMergedOptions<LinkedInProfile>(
    selectedAccount ? [selectedAccount] : [],
    followers || [],
    suggestedOptions,
  );
  const listContent = mergedOptions.length ? (
    mergedOptions.map((option) => {
      const isSelected = option.urn === selectedAccount?.urn;

      function onSelect() {
        if (isSelected) {
          setSearchParamValue(optionsKey, null);
        } else {
          setSearchParamValue(optionsKey, option);
        }
      }

      const content = (
        <>
          <ProfileImage src={option.picture_url} size="sm" />

          <div className="flex flex-col">
            <span className="text-left">{option.full_name}</span>
            <p className="line-clamp-1 break-all text-left text-xs text-black-500">
              {option.occupation}
            </p>
          </div>

          {isSelected && (
            <CheckCircle
              weight="fill"
              className="ml-auto text-purple-500 size-5"
            />
          )}
        </>
      );

      if (isDesktop) {
        return (
          <DropdownMenuItem onSelect={onSelect} key={option.urn}>
            {content}
          </DropdownMenuItem>
        );
      }
      return (
        <button
          type="button"
          className={dropdownMenuItemVariants()}
          key={option.urn}
          onClick={onSelect}
        >
          {content}
        </button>
      );
    })
  ) : (
    <p className="px-4 py-8 text-center text-black-500">No results found</p>
  );

  return isDesktop ? (
    <DropdownMenuContent className="w-80" align="start">
      <MenuSearchInput
        value={inputValue}
        placeholder="Search Followers"
        onChange={(e) => setInputValue(e.currentTarget.value)}
      />
      <DropdownMenuList>{listContent}</DropdownMenuList>
    </DropdownMenuContent>
  ) : (
    <>
      <SearchInput
        value={inputValue}
        placeholder="Search Followers"
        onChange={(e) => setInputValue(e.currentTarget.value)}
        onClear={() => setInputValue("")}
        className="mb-2"
      />
      <div className="flex flex-col">{listContent}</div>
    </>
  );
}
